// Workflow store module for workbench
import { getAllWorkflowsApi, archiveWorkflowApi } from '@/api/workflowManage';
import { notify } from '@/utils/notify';

// Fake data for Workflow Library
const fakeWorkflowLibrary = [
  {
    id: 'lib-001',
    name: 'Standard Consultation',
    description: 'Template for standard patient consultation workflow',
    thumbnail: '',
    createdAt: '2024-01-10T10:00:00.000Z',
    updatedAt: '2024-11-30T12:00:00.000Z',
    nodeCount: 10,
    edgeCount: 15,
    category: 'Consultation',
    author: 'System Admin',
    usageCount: 45
  },
  {
    id: 'lib-002',
    name: 'Emergency Triage',
    description: 'Rapid assessment and prioritization for emergency cases',
    thumbnail: '',
    createdAt: '2024-02-05T09:00:00.000Z',
    updatedAt: '2024-11-28T10:30:00.000Z',
    nodeCount: 12,
    edgeCount: 18,
    category: 'Emergency',
    author: 'Dr. Smith',
    usageCount: 32
  },
  {
    id: 'lib-003',
    name: 'Chronic Disease Management',
    description: 'Long-term care workflow for chronic conditions',
    thumbnail: '',
    createdAt: '2024-03-12T14:00:00.000Z',
    updatedAt: '2024-11-25T15:45:00.000Z',
    nodeCount: 15,
    edgeCount: 22,
    category: 'Chronic Care',
    author: 'Dr. Johnson',
    usageCount: 28
  },
  {
    id: 'lib-004',
    name: 'Preventive Care Screening',
    description: 'Comprehensive preventive health screening workflow',
    thumbnail: '',
    createdAt: '2024-04-20T11:00:00.000Z',
    updatedAt: '2024-11-20T13:20:00.000Z',
    nodeCount: 8,
    edgeCount: 11,
    category: 'Preventive',
    author: 'Nurse Manager',
    usageCount: 56
  },
  {
    id: 'lib-005',
    name: 'Surgical Pre-Op',
    description: 'Pre-operative assessment and preparation workflow',
    thumbnail: '',
    createdAt: '2024-05-15T08:00:00.000Z',
    updatedAt: '2024-11-15T09:00:00.000Z',
    nodeCount: 11,
    edgeCount: 16,
    category: 'Surgery',
    author: 'Dr. Williams',
    usageCount: 23
  },
  {
    id: 'lib-006',
    name: 'Medication Reconciliation',
    description: 'Comprehensive medication review and reconciliation',
    thumbnail: '',
    createdAt: '2024-06-10T10:00:00.000Z',
    updatedAt: '2024-11-10T11:30:00.000Z',
    nodeCount: 6,
    edgeCount: 9,
    category: 'Pharmacy',
    author: 'Pharmacist Lead',
    usageCount: 41
  },
  {
    id: 'lib-007',
    name: 'Discharge Planning',
    description: 'Patient discharge and post-care planning workflow',
    thumbnail: '',
    createdAt: '2024-07-05T13:00:00.000Z',
    updatedAt: '2024-11-05T14:15:00.000Z',
    nodeCount: 9,
    edgeCount: 13,
    category: 'Discharge',
    author: 'Care Coordinator',
    usageCount: 37
  },
  {
    id: 'lib-008',
    name: 'Telemedicine Consultation',
    description: 'Virtual consultation workflow for remote patients',
    thumbnail: '',
    createdAt: '2024-08-01T09:00:00.000Z',
    updatedAt: '2024-11-01T10:45:00.000Z',
    nodeCount: 7,
    edgeCount: 10,
    category: 'Telemedicine',
    author: 'IT Department',
    usageCount: 64
  }
];

export default {
  namespaced: true,
  
  state: {
    // My Workflows - user's custom workflows
    myWorkflows: [],
    // Workflow Library - shared/template workflows
    workflowLibrary: fakeWorkflowLibrary,
    // Current active workflow ID
    activeWorkflowId: null,
    // Loading state
    isLoading: false,
    // Error state
    error: null,
    // Patient search state (for workflow canvas)
    patientSearchTerm: '',
    searchedPatients: [],
    isLoadingPatients: false,
  },
  
  getters: {
    // Get all my workflows
    getMyWorkflows: (state) => {
      return [...state.myWorkflows].sort((a, b) => a.order - b.order);
    },
    
    // Get all workflow library items
    getWorkflowLibrary: (state) => {
      return state.workflowLibrary;
    },
    
    // Get workflow by ID (from both my workflows and library)
    getWorkflowById: (state) => (workflowId) => {
      const myWorkflow = state.myWorkflows.find(w => w.id === workflowId);
      if (myWorkflow) return myWorkflow;
      return state.workflowLibrary.find(w => w.id === workflowId);
    },
    
    // Get active workflow
    getActiveWorkflow: (state, getters) => {
      if (!state.activeWorkflowId) return null;
      return getters.getWorkflowById(state.activeWorkflowId);
    },
    
    // Check if workflow exists
    workflowExists: (state) => (workflowId) => {
      return state.myWorkflows.some(w => w.id === workflowId) || 
             state.workflowLibrary.some(w => w.id === workflowId);
    },
    
    // Get workflow count
    getMyWorkflowCount: (state) => state.myWorkflows.length,
    getLibraryWorkflowCount: (state) => state.workflowLibrary.length,
    
    // Patient search getters
    getPatientSearchTerm: (state) => state.patientSearchTerm,
    getSearchedPatients: (state) => state.searchedPatients,
    isLoadingPatients: (state) => state.isLoadingPatients,
  },
  
  mutations: {
    // Set loading state
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    
    // Set error state
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    // Set all workflows
    SET_MY_WORKFLOWS(state, workflows) {
      state.myWorkflows = workflows.map((w, index) => ({
        ...w,
        order: index
      }));
    },
    
    // Set active workflow
    SET_ACTIVE_WORKFLOW(state, workflowId) {
      state.activeWorkflowId = workflowId;
    },
    
    // Add workflow to my workflows (at the beginning)
    ADD_MY_WORKFLOW(state, workflow) {
      // Add at the beginning of the array
      state.myWorkflows.unshift({
        ...workflow,
        order: 0
      });
      // Reorder all workflows
      state.myWorkflows.forEach((w, idx) => {
        w.order = idx;
      });
    },
    
    // Update workflow in my workflows
    UPDATE_MY_WORKFLOW(state, { workflowId, updates }) {
      const index = state.myWorkflows.findIndex(w => w.id === workflowId);
      if (index !== -1) {
        state.myWorkflows[index] = {
          ...state.myWorkflows[index],
          ...updates,
          updatedAt: new Date().toISOString()
        };
      }
    },
    
    // Delete workflow from my workflows
    DELETE_MY_WORKFLOW(state, workflowId) {
      const index = state.myWorkflows.findIndex(w => w.id === workflowId);
      if (index !== -1) {
        state.myWorkflows.splice(index, 1);
        // Reorder remaining workflows
        state.myWorkflows.forEach((w, idx) => {
          w.order = idx;
        });
      }
    },
    
    // Reorder my workflows
    REORDER_MY_WORKFLOWS(state, workflows) {
      state.myWorkflows = workflows.map((w, index) => ({
        ...w,
        order: index
      }));
    },
    
    // Copy workflow from library to my workflows
    COPY_FROM_LIBRARY(state, { libraryWorkflowId, newWorkflowId }) {
      const libraryWorkflow = state.workflowLibrary.find(w => w.id === libraryWorkflowId);
      if (libraryWorkflow) {
        const newWorkflow = {
          ...libraryWorkflow,
          id: newWorkflowId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          status: 'draft',
          order: state.myWorkflows.length
        };
        // Remove library-specific fields
        delete newWorkflow.category;
        delete newWorkflow.author;
        delete newWorkflow.usageCount;
        
        state.myWorkflows.push(newWorkflow);
      }
    },
    
    // Patient search mutations
    SET_PATIENT_SEARCH_TERM(state, searchTerm) {
      state.patientSearchTerm = searchTerm;
    },
    
    SET_SEARCHED_PATIENTS(state, patients) {
      state.searchedPatients = patients;
    },
    
    APPEND_SEARCHED_PATIENTS(state, patients) {
      state.searchedPatients = [...state.searchedPatients, ...patients];
    },
    
    SET_LOADING_PATIENTS(state, isLoading) {
      state.isLoadingPatients = isLoading;
    },
    
    CLEAR_PATIENT_SEARCH(state) {
      state.patientSearchTerm = '';
      state.searchedPatients = [];
      state.isLoadingPatients = false;
    },
  },
  
  actions: {
    // Initialize workflows from API
    async initializeWorkflows({ commit }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await getAllWorkflowsApi();
        const workflows = response.data || [];
        commit('SET_MY_WORKFLOWS', workflows);
        return workflows;
      } catch (error) {
        console.error('Error fetching workflows:', error);
        commit('SET_ERROR', error.message || 'Failed to fetch workflows');
        commit('SET_MY_WORKFLOWS', []);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    // Set active workflow
    setActiveWorkflow({ commit }, workflowId) {
      commit('SET_ACTIVE_WORKFLOW', workflowId);
    },
    
    // Add new workflow
    addMyWorkflow({ commit }, workflow) {
      commit('ADD_MY_WORKFLOW', workflow);
    },
    
    // Update workflow
    updateMyWorkflow({ commit }, payload) {
      commit('UPDATE_MY_WORKFLOW', payload);
    },
    
    // Archive workflow (no real delete, only archive)
    async deleteMyWorkflow({ commit }, workflowId) {
      try {
        console.log('Archiving workflow:', workflowId);
        
        // Call archive API
        await archiveWorkflowApi(workflowId);
        
        // Remove from local state after successful archive
        commit('DELETE_MY_WORKFLOW', workflowId);
        
        notify({
          title: 'Success',
          message: 'Workflow archived successfully',
          type: 'success',
          duration: 3000
        });
      } catch (error) {
        console.error('Failed to archive workflow:', error);
        notify({
          title: 'Error',
          message: error.response?.data?.message || 'Failed to archive workflow',
          type: 'error',
          duration: 5000
        });
        throw error;
      }
    },
    
    // Reorder workflows
    reorderMyWorkflows({ commit }, workflows) {
      commit('REORDER_MY_WORKFLOWS', workflows);
    },
    
    // Copy from library
    copyFromLibrary({ commit }, payload) {
      commit('COPY_FROM_LIBRARY', payload);
    },
    
    // Patient search actions
    setPatientSearchTerm({ commit }, searchTerm) {
      commit('SET_PATIENT_SEARCH_TERM', searchTerm);
    },
    
    setSearchedPatients({ commit }, patients) {
      commit('SET_SEARCHED_PATIENTS', patients);
    },
    
    appendSearchedPatients({ commit }, patients) {
      commit('APPEND_SEARCHED_PATIENTS', patients);
    },
    
    setLoadingPatients({ commit }, isLoading) {
      commit('SET_LOADING_PATIENTS', isLoading);
    },
    
    clearPatientSearch({ commit }) {
      commit('CLEAR_PATIENT_SEARCH');
    },
  },
};
